import React, {
  useCallback,
} from 'react';
import PropTypes from 'prop-types';
import { withRouter } from 'react-router-dom';
import ReactRouterPropTypes from 'react-router-prop-types';
import { FormattedMessage } from 'react-intl';

import { NoValue } from '@folio/stripes/components';
import {
  JOB_FIELDS,
  JOB_SEARCHABLE_INDEXES,
} from '../../../constants';
import {
  FolioFormattedDate,
  SearchAndFilter,
} from '../../common';
import { openItemDetail } from '../../../utils';

const entity = 'job';

const visibleColumns = [
  `${entity}-${JOB_FIELDS.NAME}`,
  `${entity}-${JOB_FIELDS.START_DATE}`,
  `${entity}-${JOB_FIELDS.DUE_DATE}`,
  `${entity}-${JOB_FIELDS.TOTAL_TASKS}`,
  `${entity}-${JOB_FIELDS.STATUS}`,
];

const columnMapping = {
  [`${entity}-${JOB_FIELDS.NAME}`]: <FormattedMessage id="ui-task-list.job.field.name" />,
  [`${entity}-${JOB_FIELDS.START_DATE}`]: <FormattedMessage id="ui-task-list.job.list.field.startDate" />,
  [`${entity}-${JOB_FIELDS.DUE_DATE}`]: <FormattedMessage id="ui-task-list.job.list.field.dueDate" />,
  [`${entity}-${JOB_FIELDS.TOTAL_TASKS}`]: <FormattedMessage id="ui-task-list.job.list.field.totalTasks" />,
  [`${entity}-${JOB_FIELDS.STATUS}`]: <FormattedMessage id="ui-task-list.job.list.field.status" />,
};

const resultsFormatter = {
  [`${entity}-${JOB_FIELDS.NAME}`]: data => data[JOB_FIELDS.NAME],
  [`${entity}-${JOB_FIELDS.STATUS}`]: data => <FormattedMessage id={`ui-task-list.job.statuses.${data[JOB_FIELDS.STATUS]}`} />,
  [`${entity}-${JOB_FIELDS.START_DATE}`]: data => (data[JOB_FIELDS.START_DATE]
    ? <FolioFormattedDate value={data[JOB_FIELDS.START_DATE]} />
    : <NoValue />
  ),
  [`${entity}-${JOB_FIELDS.DUE_DATE}`]: data => (data[JOB_FIELDS.DUE_DATE]
    ? <FolioFormattedDate value={data[JOB_FIELDS.DUE_DATE]} />
    : <NoValue />
  ),
  [`${entity}-${JOB_FIELDS.TOTAL_TASKS}`]: data => data[JOB_FIELDS.TOTAL_TASKS],
};

const resultsPaneTitle = <FormattedMessage id="ui-task-list.title.list.job" />;

const JobList = ({
  history,
  match,
  location,
  onNeedMoreData,
  resetData,
  jobs,
  jobsCount,
  isLoading,
  children,
}) => {
  const openJobDetails = useCallback(
    (e, meta) => {
      openItemDetail({ history, path: match.path, location, id: meta.id });
    },
    [location.search, location.state],
  );

  return (
    <SearchAndFilter
      columnMapping={columnMapping}
      contentData={jobs}
      count={jobsCount}
      id="jobs-list"
      isLoading={isLoading}
      resetData={resetData}
      resultsFormatter={resultsFormatter}
      resultsPaneTitle={resultsPaneTitle}
      searchableIndexes={JOB_SEARCHABLE_INDEXES}
      visibleColumns={visibleColumns}
      onNeedMoreData={onNeedMoreData}
      onRowClick={openJobDetails}
    >
      { children }
    </SearchAndFilter>
  );
};

JobList.propTypes = {
  history: ReactRouterPropTypes.history.isRequired,
  location: ReactRouterPropTypes.location.isRequired,
  match: ReactRouterPropTypes.match.isRequired,
  resetData: PropTypes.func.isRequired,
  onNeedMoreData: PropTypes.func.isRequired,
  children: PropTypes.node,
  isLoading: PropTypes.bool,
  jobs: PropTypes.arrayOf(PropTypes.object),
  jobsCount: PropTypes.number
};

JobList.defaultProps = {
  jobsCount: 0,
  isLoading: false,
  jobs: [],
};

export default withRouter(JobList);
